import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  AttachMoney as AttachMoneyIcon,
} from "@material-ui/icons";
import { makeStyles, alpha } from "@material-ui/core/styles";
import { blue } from "@material-ui/core/colors";
import { isDarkTheme } from "../../helpers/darkTint";
import { toast } from "react-toastify";
import { createBoard, updateBoard } from "../../services/kanban";
import { iconOptions } from "./ColumnModal";
import { IMOBILIZADO_DATA_SOURCE } from "./ImobilizadoAutocomplete";

const useStyles = makeStyles((theme) => ({
  fieldItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#e0e0e0"}`,
    borderRadius: 4,
    backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : "#fafafa",
  },
  fieldHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  dragHandle: {
    cursor: "move",
    marginRight: theme.spacing(1),
  },
  fieldTypeChip: {
    marginLeft: theme.spacing(1),
  },
  optionItem: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  defaultFieldsInfo: {
    padding: theme.spacing(2),
    backgroundColor: isDarkTheme(theme) ? alpha(blue[500], 0.2) : "#e3f2fd",
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  inactivityPaper: {
    padding: 16,
    backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : "#f5f5f5",
  },
}));

// Campos padrão sugeridos
const DEFAULT_FIELDS = [
  {
    fieldName: "title",
    fieldLabel: "Título",
    fieldType: "text",
    isRequired: true,
    alwaysVisible: true,
    fieldOrder: 1,
    placeholder: "Digite o título do card",
    helpText: "Título principal que aparecerá no card",
  },
  {
    fieldName: "description",
    fieldLabel: "Descrição",
    fieldType: "textarea",
    isRequired: false,
    alwaysVisible: true,
    fieldOrder: 2,
    placeholder: "Descreva os detalhes do card",
    helpText: "Informações adicionais sobre o card",
  },
  {
    fieldName: "priority",
    fieldLabel: "Prioridade",
    fieldType: "select",
    isRequired: false,
    alwaysVisible: false,
    fieldOrder: 3,
    defaultValue: "medium",
    fieldOptions: [
      { value: "low", label: "Baixa" },
      { value: "medium", label: "Média" },
      { value: "high", label: "Alta" },
      { value: "urgent", label: "Urgente" },
    ],
    helpText: "Nível de prioridade do card",
  },
  {
    fieldName: "assignedUser",
    fieldLabel: "Responsável",
    fieldType: "select",
    isRequired: false,
    alwaysVisible: false,
    fieldOrder: 4,
    placeholder: "Selecione um usuário",
    helpText: "Usuário responsável pelo card",
  },
  {
    fieldName: "dueDate",
    fieldLabel: "Data de Vencimento",
    fieldType: "date",
    isRequired: false,
    alwaysVisible: false,
    fieldOrder: 5,
    helpText: "Data limite para conclusão",
  },
  {
    fieldName: "headerColor",
    fieldLabel: "Cor do Header",
    fieldType: "color",
    isRequired: false,
    alwaysVisible: false,
    fieldOrder: 6,
    defaultValue: "#3f51b5",
    placeholder: "#000000",
    helpText: "Cor personalizada para o cabeçalho do card",
  },
  {
    fieldName: "estimatedValue",
    fieldLabel: "Valor Estimado",
    fieldType: "money",
    isRequired: false,
    alwaysVisible: false,
    fieldOrder: 7,
    placeholder: "R$ 0,00",
    helpText: "Valor estimado do negócio",
  },
  {
    fieldName: "contactEmail",
    fieldLabel: "E-mail do Contato",
    fieldType: "email",
    isRequired: false,
    alwaysVisible: false,
    fieldOrder: 8,
    placeholder: "email@exemplo.com",
    helpText: "E-mail principal do contato",
  },
  {
    fieldName: "contactPhone",
    fieldLabel: "Telefone do Contato",
    fieldType: "text",
    isRequired: false,
    alwaysVisible: false,
    fieldOrder: 9,
    placeholder: "(00) 00000-0000",
    helpText: "Telefone principal do contato",
  },
  {
    fieldName: "valorReais",
    fieldLabel: "Valor R$",
    fieldType: "money",
    isRequired: false,
    alwaysVisible: true,
    fieldOrder: 10,
    placeholder: "R$ 0,00",
    helpText: "Valor em reais",
  },
];

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "textarea", label: "Texto Longo" },
  { value: "number", label: "Número" },
  { value: "email", label: "E-mail" },
  { value: "money", label: "Valor Monetário" },
  { value: "date", label: "Data" },
  { value: "color", label: "Cor" },
  { value: "select", label: "Seleção Única (Dropdown)" },
  { value: "radio", label: "Seleção Única (Radio)" },
  { value: "multiselect", label: "Seleção Múltipla (Dropdown)" },
  { value: "checkbox", label: "Seleção Múltipla (Checkbox)" },
];

// Fontes externas que transformam um campo de texto em autocomplete.
// Gravadas em validation.dataSource, sem mexer no tipo do campo — assim o campo
// continua sendo "text" para filtros, relatórios e para a integração empsis.
const FIELD_DATA_SOURCES = [
  { value: "", label: "Nenhuma" },
  { value: IMOBILIZADO_DATA_SOURCE, label: "Imobilizados (Empsis)" },
];

const FieldOption = ({ option, index, onChange, onRemove }) => {
  const classes = useStyles();

  return (
    <Box className={classes.optionItem}>
      <TextField
        label="Valor"
        value={option.value}
        onChange={(e) => onChange(index, "value", e.target.value)}
        size="small"
        variant="outlined"
        fullWidth
      />
      <TextField
        label="Rótulo"
        value={option.label}
        onChange={(e) => onChange(index, "label", e.target.value)}
        size="small"
        variant="outlined"
        fullWidth
      />
      <IconButton size="small" onClick={() => onRemove(index)} color="secondary">
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};

const FieldDefinitionItem = ({ field, index, onChange, onRemove }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const handleFieldChange = (prop, value) => {
    const updatedField = { ...field, [prop]: value };
    
    // Se o tipo de campo for alterado para select, radio, multiselect ou checkbox, garantir que fieldOptions seja um array
    if (prop === "fieldType" && ["select", "radio", "multiselect", "checkbox"].includes(value)) {
      if (!Array.isArray(updatedField.fieldOptions)) {
        updatedField.fieldOptions = [];
      }
      
      // Converter defaultValue baseado no novo tipo
      if (value === "multiselect" || value === "checkbox") {
        // Converte para array se não for
        if (!Array.isArray(updatedField.defaultValue)) {
          updatedField.defaultValue = updatedField.defaultValue ? [updatedField.defaultValue] : [];
        }
      } else if (value === "select" || value === "radio") {
        // Converte para string se for array
        if (Array.isArray(updatedField.defaultValue)) {
          updatedField.defaultValue = updatedField.defaultValue.length > 0 ? updatedField.defaultValue[0] : "";
        }
      }
    }
    
    // Se o tipo não precisa de opções, limpar defaultValue se for array
    if (prop === "fieldType" && !["select", "radio", "multiselect", "checkbox"].includes(value)) {
      if (Array.isArray(updatedField.defaultValue)) {
        updatedField.defaultValue = "";
      }
    }
    
    onChange(index, updatedField);
  };

  // Preserva o resto do objeto validation (min/max/maxLength...) ao ligar/desligar
  // a fonte de dados; sem fonte, remove a chave em vez de gravar string vazia.
  const handleDataSourceChange = (dataSource) => {
    const { dataSource: _atual, ...resto } = field.validation || {};
    const validation = dataSource ? { ...resto, dataSource } : resto;

    handleFieldChange(
      "validation",
      Object.keys(validation).length > 0 ? validation : null
    );
  };

  const handleOptionChange = (optIndex, prop, value) => {
    const newOptions = [...(field.fieldOptions || [])];
    newOptions[optIndex] = { ...newOptions[optIndex], [prop]: value };
    handleFieldChange("fieldOptions", newOptions);
  };

  const handleAddOption = () => {
    const newOptions = [...(field.fieldOptions || []), { value: "", label: "" }];
    handleFieldChange("fieldOptions", newOptions);
  };

  const handleRemoveOption = (optIndex) => {
    const newOptions = (field.fieldOptions || []).filter((_, i) => i !== optIndex);
    handleFieldChange("fieldOptions", newOptions);
  };

  const needsOptions = ["select", "radio", "multiselect", "checkbox"].includes(field.fieldType);

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className={classes.fieldHeader} style={{ width: "100%" }}>
          <DragIcon className={classes.dragHandle} />
          <Typography variant="subtitle1" style={{ flex: 1 }}>
            {field.fieldLabel || "Novo Campo"}
            {field.isRequired && " *"}
          </Typography>
          <Chip
            label={FIELD_TYPES.find((t) => t.value === field.fieldType)?.label || field.fieldType}
            size="small"
            className={classes.fieldTypeChip}
          />
          <IconButton size="small" onClick={() => onRemove(index)} color="secondary">
            <DeleteIcon />
          </IconButton>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome do Campo"
              value={field.fieldName}
              onChange={(e) => handleFieldChange("fieldName", e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Rótulo"
              value={field.fieldLabel}
              onChange={(e) => handleFieldChange("fieldLabel", e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Tipo de Campo</InputLabel>
              <Select
                value={field.fieldType}
                onChange={(e) => handleFieldChange("fieldType", e.target.value)}
                label="Tipo de Campo"
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {field.fieldType === "text" && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Fonte de Dados</InputLabel>
                <Select
                  value={field.validation?.dataSource || ""}
                  onChange={(e) => handleDataSourceChange(e.target.value)}
                  label="Fonte de Dados"
                >
                  {FIELD_DATA_SOURCES.map((source) => (
                    <MenuItem key={source.value || "none"} value={source.value}>
                      {source.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Transforma o campo de texto em busca com sugestões
                </FormHelperText>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.isRequired}
                  onChange={(e) => handleFieldChange("isRequired", e.target.checked)}
                  color="primary"
                />
              }
              label="Campo Obrigatório"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.alwaysVisible !== false}
                  onChange={(e) => handleFieldChange("alwaysVisible", e.target.checked)}
                  color="primary"
                />
              }
              label="Sempre Visível"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Placeholder"
              value={field.placeholder || ""}
              onChange={(e) => handleFieldChange("placeholder", e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* Valor Padrão Dinâmico baseado no Tipo de Campo */}
            {needsOptions ? (
              // Para campos de seleção (select, radio, multiselect, checkbox)
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Valor Padrão</InputLabel>
                <Select
                  value={
                    field.fieldType === "multiselect" || field.fieldType === "checkbox"
                      ? Array.isArray(field.defaultValue) 
                        ? field.defaultValue 
                        : (field.defaultValue ? [field.defaultValue] : [])
                      : field.defaultValue || ""
                  }
                  onChange={(e) => handleFieldChange("defaultValue", e.target.value)}
                  label="Valor Padrão"
                  multiple={field.fieldType === "multiselect" || field.fieldType === "checkbox"}
                >
                  {!(field.fieldType === "multiselect" || field.fieldType === "checkbox") && (
                    <MenuItem value="">
                      <em>Nenhum</em>
                    </MenuItem>
                  )}
                  {(field.fieldOptions || []).map((option, idx) => (
                    <MenuItem key={idx} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : field.fieldType === "color" ? (
              // Para campos de cor
              <Box>
                <InputLabel shrink style={{ marginBottom: 4, fontSize: 12 }}>
                  Valor Padrão
                </InputLabel>
                <TextField
                  type="color"
                  value={field.defaultValue || "#000000"}
                  onChange={(e) => handleFieldChange("defaultValue", e.target.value)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            ) : field.fieldType === "date" ? (
              // Para campos de data
              <TextField
                label="Valor Padrão"
                type="date"
                value={field.defaultValue || ""}
                onChange={(e) => handleFieldChange("defaultValue", e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            ) : field.fieldType === "money" ? (
              // Para campos monetários - texto simples que será formatado no card
              <TextField
                label="Valor Padrão (apenas números)"
                value={field.defaultValue || ""}
                onChange={(e) => {
                  // Permite apenas números e ponto decimal
                  const value = e.target.value.replace(/[^\d.]/g, "");
                  handleFieldChange("defaultValue", value);
                }}
                fullWidth
                size="small"
                variant="outlined"
                placeholder="0.00"
                helperText="Digite apenas números. Ex: 100.50"
              />
            ) : field.fieldType === "number" ? (
              // Para campos numéricos
              <TextField
                label="Valor Padrão"
                type="number"
                value={field.defaultValue || ""}
                onChange={(e) => handleFieldChange("defaultValue", e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
              />
            ) : (
              // Para campos de texto, textarea, email
              <TextField
                label="Valor Padrão"
                value={field.defaultValue || ""}
                onChange={(e) => handleFieldChange("defaultValue", e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Texto de Ajuda"
              value={field.helpText || ""}
              onChange={(e) => handleFieldChange("helpText", e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              multiline
              rows={2}
            />
          </Grid>

          {needsOptions && (
            <Grid item xs={12}>
              <Divider style={{ margin: "16px 0" }} />
              <Typography variant="subtitle2" gutterBottom>
                Opções do Campo
              </Typography>
              {(field.fieldOptions || []).map((option, optIndex) => (
                <FieldOption
                  key={optIndex}
                  option={option}
                  index={optIndex}
                  onChange={handleOptionChange}
                  onRemove={handleRemoveOption}
                />
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddOption}
                size="small"
                color="primary"
              >
                Adicionar Opção
              </Button>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

const BoardModal = ({ open, onClose, board, onSuccess }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "AttachMoney",
    isMain: false,
    inactivityNotificationEmail: "",
    enableInactivityNotification: false,
    inactivityDays: 30,
  });
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [useDefaultFields, setUseDefaultFields] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (board) {
      console.log('Board carregado no BoardModal:', board);
      console.log('Field Definitions do board:', board.fieldDefinitions);
      
      setFormData({
        name: board.name || "",
        description: board.description || "",
        icon: board.icon || "AttachMoney",
        isMain: board.isMain || false,
        inactivityNotificationEmail: board.inactivityNotificationEmail || "",
        enableInactivityNotification: board.enableInactivityNotification || false,
        inactivityDays: board.inactivityDays || 30,
      });
      // Garantir que fieldOptions seja sempre um array e deserializar defaultValue
      const normalizedFields = (board.fieldDefinitions || []).map(field => {
        const normalizedField = {
          ...field,
          fieldOptions: Array.isArray(field.fieldOptions) 
            ? field.fieldOptions 
            : (field.fieldOptions ? [] : [])
        };
        
        // Deserializar defaultValue se for JSON string de array
        // Apenas para campos multiselect e checkbox
        if ((field.fieldType === 'multiselect' || field.fieldType === 'checkbox') && 
            typeof field.defaultValue === 'string' && 
            field.defaultValue.trim().startsWith('[')) {
          try {
            normalizedField.defaultValue = JSON.parse(field.defaultValue);
          } catch (e) {
            console.error('Erro ao fazer parse de defaultValue:', e);
            normalizedField.defaultValue = [];
          }
        }
        
        return normalizedField;
      });
      
      console.log('Field Definitions normalizados no BoardModal:', normalizedFields);
      setFieldDefinitions(normalizedFields);
      setUseDefaultFields(false);
    } else {
      setFormData({
        name: "",
        description: "",
        icon: "AttachMoney",
        isMain: false,
        inactivityNotificationEmail: "",
        enableInactivityNotification: false,
        inactivityDays: 30,
      });
      setFieldDefinitions([]);
      setUseDefaultFields(true);
    }
  }, [board, open]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFieldChange = (index, updatedField) => {
    const newFields = [...fieldDefinitions];
    newFields[index] = updatedField;
    setFieldDefinitions(newFields);
  };

  const handleAddField = () => {
    setFieldDefinitions([
      ...fieldDefinitions,
      {
        fieldName: `campo_${fieldDefinitions.length + 1}`,
        fieldLabel: "Novo Campo",
        fieldType: "text",
        isRequired: false,
        alwaysVisible: true,
        fieldOrder: fieldDefinitions.length,
        placeholder: "",
        helpText: "",
        defaultValue: "",
        fieldOptions: [],
      },
    ]);
    setUseDefaultFields(false);
  };

  const handleRemoveField = (index) => {
    setFieldDefinitions(fieldDefinitions.filter((_, i) => i !== index));
  };

  const handleUseDefaultFields = () => {
    setFieldDefinitions(DEFAULT_FIELDS);
    setUseDefaultFields(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("O nome é obrigatório");
      return;
    }

    setLoading(true);

    try {
      // Serializar defaultValue de arrays para JSON string antes de enviar
      const normalizedFieldDefinitions = fieldDefinitions.map(field => {
        const normalizedField = { ...field };
        
        // Se defaultValue é um array, serializar como JSON string
        if (Array.isArray(normalizedField.defaultValue)) {
          normalizedField.defaultValue = JSON.stringify(normalizedField.defaultValue);
        }
        
        return normalizedField;
      });

      const dataToSend = {
        ...formData,
        icon: formData.icon || "AttachMoney",
        fieldDefinitions: useDefaultFields && fieldDefinitions.length === 0 
          ? undefined 
          : normalizedFieldDefinitions,
      };

      console.log("[KANBAN][BoardModal] submit", {
        mode: board ? "update" : "create",
        boardId: board?.id,
        icon: dataToSend.icon,
        name: dataToSend.name,
      });

      if (board) {
        await updateBoard(board.id, dataToSend);
        toast.success("Board atualizado com sucesso!");
      } else {
        await createBoard(dataToSend);
        toast.success("Board criado com sucesso!");
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Erro ao salvar board");
    } finally {
      setLoading(false);
    }
  };

  const selectedIconOption = iconOptions.find((option) => option.name === formData.icon);
  const SelectedIconComponent = selectedIconOption?.icon || AttachMoneyIcon;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{board ? "Editar Board" : "Novo Board"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Nome *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                margin="dense"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descrição"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                margin="dense"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>Ícone</InputLabel>
                <Select
                  value={formData.icon}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, icon: e.target.value }))
                  }
                  label="Ícone"
                >
                  {iconOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <MenuItem key={option.name} value={option.name}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconComponent fontSize="small" />
                          <Typography>{option.label}</Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                padding={2}
                border={1}
                borderColor="divider"
                borderRadius={4}
                height="100%"
                minHeight={56}
              >
                <Typography variant="body2">Preview:</Typography>
                <SelectedIconComponent fontSize="small" />
                <Typography style={{ fontWeight: 600 }}>
                  {formData.name || "Nome do Board"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isMain}
                    onChange={handleChange}
                    name="isMain"
                    color="primary"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">
                      Definir como Board Principal
                    </Typography>
                    <Tooltip title="Apenas um board pode ser o principal. Se você marcar esta opção, todos os outros boards serão automaticamente desmarcados.">
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Box className={classes.sectionTitle}>
                <Typography variant="h6">Notificação de Inatividade</Typography>
                <Tooltip title="Configure notificações quando o fluxo não receber novos cards por um período">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>

              <Paper className={classes.inactivityPaper}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.enableInactivityNotification}
                          onChange={handleChange}
                          name="enableInactivityNotification"
                          color="primary"
                        />
                      }
                      label="Ativar notificação de inatividade"
                    />
                  </Grid>

                  {formData.enableInactivityNotification && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          label="Email(s) para Notificação"
                          name="inactivityNotificationEmail"
                          value={formData.inactivityNotificationEmail}
                          onChange={handleChange}
                          fullWidth
                          variant="outlined"
                          placeholder="email@exemplo.com ou email1@exemplo.com,email2@exemplo.com"
                          helperText="Separe múltiplos emails com vírgula"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Dias sem movimentação"
                          name="inactivityDays"
                          type="number"
                          value={formData.inactivityDays}
                          onChange={handleChange}
                          fullWidth
                          variant="outlined"
                          inputProps={{ min: 1, max: 365 }}
                          helperText="Período em dias sem novos cards para enviar notificação"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Importante:</strong> A verificação é executada diariamente às 9h.
                          Você receberá apenas uma notificação por mês para evitar spam.
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box className={classes.sectionTitle}>
                <Typography variant="h6">Campos dos Cards</Typography>
                <Tooltip title="Configure quais campos os cards deste board terão">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>

              {!board && (
                <Paper className={classes.defaultFieldsInfo}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Campos Padrão Sugeridos:</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Se você não adicionar campos personalizados, o board será criado com 8 campos
                    padrão: Título, Descrição, Prioridade, Responsável, Data de Vencimento, Valor
                    Estimado, E-mail do Contato e Telefone do Contato.
                  </Typography>
                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleUseDefaultFields}
                    >
                      Usar Campos Padrão
                    </Button>
                  </Box>
                </Paper>
              )}

              {fieldDefinitions.map((field, index) => (
                <FieldDefinitionItem
                  key={index}
                  field={field}
                  index={index}
                  onChange={handleFieldChange}
                  onRemove={handleRemoveField}
                />
              ))}

              <Box mt={2}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddField}
                  variant="outlined"
                  color="primary"
                >
                  Adicionar Campo Customizado
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="default">
            Cancelar
          </Button>
          <Button type="submit" color="primary" disabled={loading}>
            {loading ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BoardModal;

